import { useState } from "react";
import {
  Box, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Alert, TextField,
  Select, MenuItem, FormControl, InputLabel,
  Tabs, Tab,
} from "@mui/material";
import {
  Add,
  BatteryChargingFull,
  Cable,
  DeviceHub,
  Monitor,
  Print,
  Refresh,
  SettingsInputComponent,
  Storage,
  Usb,
  UsbOff,
} from "@mui/icons-material";
import type { SvgIconComponent } from "@mui/icons-material";
import { Page, PageHeader } from "../../components/layout/Page";
import { useQuery } from "@tanstack/react-query";
import { toast } from "../../utils/notifications";
import { fetchCuratedPrices } from "../../api/priceTables";
import { useAdminCrud } from "../../hooks/useAdminCrud";
import {
  ActionsHeadCell,
  DataCard,
  DataPagination,
  DataToolbar,
  RowActions,
  SearchField,
  TableMessageRow,
} from "./DataTableParts";
import { FORM_GRID_SX, FULL_SPAN_SX, TIGHT_COL_SX, formatMoney } from "./dataTableStyles";
import { queryKeys } from "../../api/queryKeys";
import type { ErrorLike } from "../../utils/errors";

// ============================================================================
// Category Definitions — column config per category tab
// ============================================================================

interface CategoryConfig {
  key: string;
  label: string;
  icon: SvgIconComponent;
  /** `numeric` and price columns are right-aligned and kept tight. */
  columns: { field: string; label: string; type?: string; numeric?: boolean }[];
  formFields: { field: string; label: string; type: string; options?: string[] }[];
}

const CATEGORIES: CategoryConfig[] = [
  {
    key: "cable", label: "Cable", icon: Cable,
    columns: [
      { field: "brand", label: "Brand" },
      { field: "model", label: "Model" },
      { field: "category", label: "Type" },
      { field: "length_meters", label: "Length (m)", numeric: true },
      { field: "connector_side_a", label: "Side A" },
      { field: "connector_side_b", label: "Side B" },
      { field: "shielded", label: "Shielded" },
      { field: "price", label: "Price", type: "price" },
      { field: "price_unit", label: "Unit" },
    ],
    formFields: [
      { field: "brand", label: "Brand", type: "text" },
      { field: "model", label: "Model", type: "text" },
      { field: "category", label: "Category", type: "select", options: ["network", "hdmi", "display", "usb", "other"] },
      { field: "cable_type", label: "Cable Type", type: "text" },
      { field: "length_meters", label: "Length (meters)", type: "number" },
      { field: "connector_side_a", label: "Connector Side A", type: "text" },
      { field: "connector_side_b", label: "Connector Side B", type: "text" },
      { field: "shielded", label: "Shielded", type: "select", options: ["false", "true"] },
      { field: "price", label: "Price", type: "number" },
      { field: "price_unit", label: "Price Unit", type: "select", options: ["each", "per_meter", "per_set", "per_box"] },
      { field: "keywords", label: "Keywords (comma-sep)", type: "text" },
      { field: "notes", label: "Notes", type: "text" },
    ],
  },
  {
    key: "ups", label: "UPS", icon: BatteryChargingFull,
    columns: [
      { field: "brand", label: "Brand" },
      { field: "model", label: "Model" },
      { field: "va_rating", label: "VA", numeric: true },
      { field: "watts", label: "Watts", numeric: true },
      { field: "voltage", label: "Voltage", numeric: true },
      { field: "ups_type", label: "Type" },
      { field: "rack_mountable", label: "Rack" },
      { field: "price", label: "Price", type: "price" },
    ],
    formFields: [
      { field: "brand", label: "Brand", type: "text" },
      { field: "model", label: "Model", type: "text" },
      { field: "va_rating", label: "VA Rating", type: "number" },
      { field: "watts", label: "Watts", type: "number" },
      { field: "voltage", label: "Voltage", type: "number" },
      { field: "ups_type", label: "UPS Type", type: "select", options: ["Backup", "Line-Interactive", "Smart", "Online", "Rack", "Battery Module"] },
      { field: "rack_mountable", label: "Rack Mountable", type: "select", options: ["false", "true"] },
      { field: "marine", label: "Marine", type: "select", options: ["false", "true"] },
      { field: "price", label: "Price", type: "number" },
      { field: "price_unit", label: "Unit", type: "select", options: ["each", "per_set"] },
      { field: "keywords", label: "Keywords (comma-sep)", type: "text" },
      { field: "notes", label: "Notes", type: "text" },
    ],
  },
  {
    key: "monitor", label: "Monitor", icon: Monitor,
    columns: [
      { field: "brand", label: "Brand" },
      { field: "model", label: "Model" },
      { field: "size_inches", label: 'Size', numeric: true },
      { field: "resolution", label: "Resolution" },
      { field: "panel_type", label: "Panel" },
      { field: "marine", label: "Marine" },
      { field: "price", label: "Price", type: "price" },
    ],
    formFields: [
      { field: "brand", label: "Brand", type: "text" },
      { field: "model", label: "Model", type: "text" },
      { field: "size_inches", label: 'Size (inches)', type: "number" },
      { field: "resolution", label: "Resolution", type: "select", options: ["HD", "FHD", "QHD", "4K UHD", "SXGA", "WUXGA"] },
      { field: "panel_type", label: "Panel Type", type: "select", options: ["IPS", "VA", "TN", "LED", "TFT"] },
      { field: "marine", label: "Marine", type: "select", options: ["false", "true"] },
      { field: "price", label: "Price", type: "number" },
      { field: "price_unit", label: "Unit", type: "select", options: ["each", "per_set"] },
      { field: "keywords", label: "Keywords (comma-sep)", type: "text" },
      { field: "notes", label: "Notes", type: "text" },
    ],
  },
  {
    key: "usb_blocker", label: "USB Blocker", icon: UsbOff,
    columns: [
      { field: "brand", label: "Brand" },
      { field: "model", label: "Model" },
      { field: "blocker_type", label: "Type" },
      { field: "pack_qty", label: "Pack Qty", numeric: true },
      { field: "includes_key", label: "Key" },
      { field: "price", label: "Price", type: "price" },
    ],
    formFields: [
      { field: "brand", label: "Brand", type: "text" },
      { field: "model", label: "Model", type: "text" },
      { field: "blocker_type", label: "Blocker Type", type: "select", options: ["USB-A", "USB-C", "RJ45"] },
      { field: "pack_qty", label: "Pack Quantity", type: "number" },
      { field: "includes_key", label: "Includes Key", type: "select", options: ["false", "true"] },
      { field: "price", label: "Price", type: "number" },
      { field: "price_unit", label: "Unit", type: "select", options: ["each", "per_set"] },
      { field: "keywords", label: "Keywords (comma-sep)", type: "text" },
      { field: "notes", label: "Notes", type: "text" },
    ],
  },
  {
    key: "printer", label: "Printer", icon: Print,
    columns: [
      { field: "brand", label: "Brand" },
      { field: "model", label: "Model" },
      { field: "printer_type", label: "Type" },
      { field: "price", label: "Price", type: "price" },
    ],
    formFields: [
      { field: "brand", label: "Brand", type: "text" },
      { field: "model", label: "Model", type: "text" },
      { field: "printer_type", label: "Printer Type", type: "select", options: ["Inkjet", "Laser", "Ink Tank", "Thermal", "Dot Matrix", "Label", "All-in-One"] },
      { field: "price", label: "Price", type: "number" },
      { field: "price_unit", label: "Unit", type: "select", options: ["each"] },
      { field: "keywords", label: "Keywords (comma-sep)", type: "text" },
      { field: "notes", label: "Notes", type: "text" },
    ],
  },
  {
    key: "connector", label: "Connector", icon: SettingsInputComponent,
    columns: [
      { field: "brand", label: "Brand" },
      { field: "model", label: "Model" },
      { field: "connector_type", label: "Type" },
      { field: "gender_male", label: "Male" },
      { field: "adapter", label: "Adapter" },
      { field: "price", label: "Price", type: "price" },
    ],
    formFields: [
      { field: "brand", label: "Brand", type: "text" },
      { field: "model", label: "Model", type: "text" },
      { field: "connector_type", label: "Connector Type", type: "text" },
      { field: "gender_male", label: "Male Gender", type: "select", options: ["true", "false"] },
      { field: "adapter", label: "Is Adapter", type: "select", options: ["false", "true"] },
      { field: "price", label: "Price", type: "number" },
      { field: "price_unit", label: "Unit", type: "select", options: ["each", "per_set"] },
      { field: "keywords", label: "Keywords (comma-sep)", type: "text" },
      { field: "notes", label: "Notes", type: "text" },
    ],
  },
  {
    key: "flash_drive", label: "Flash Drive", icon: Usb,
    columns: [
      { field: "brand", label: "Brand" },
      { field: "model", label: "Model" },
      { field: "capacity_gb", label: "Capacity (GB)", numeric: true },
      { field: "usb_type", label: "USB Type" },
      { field: "price", label: "Price", type: "price" },
    ],
    formFields: [
      { field: "brand", label: "Brand", type: "text" },
      { field: "model", label: "Model", type: "text" },
      { field: "capacity_gb", label: "Capacity (GB)", type: "number" },
      { field: "usb_type", label: "USB Type", type: "select", options: ["USB-A", "USB-C", "USB-A+C"] },
      { field: "usb_speed", label: "USB Speed", type: "select", options: ["USB 2.0", "USB 3.0", "USB 3.1", "USB 3.2"] },
      { field: "price", label: "Price", type: "number" },
      { field: "price_unit", label: "Unit", type: "select", options: ["each", "per_set"] },
      { field: "keywords", label: "Keywords (comma-sep)", type: "text" },
      { field: "notes", label: "Notes", type: "text" },
    ],
  },
  {
    key: "usb_hub", label: "USB Hub", icon: DeviceHub,
    columns: [
      { field: "brand", label: "Brand" },
      { field: "model", label: "Model" },
      { field: "port_count", label: "Ports", numeric: true },
      { field: "usb_type", label: "USB Type" },
      { field: "powered", label: "Powered" },
      { field: "price", label: "Price", type: "price" },
    ],
    formFields: [
      { field: "brand", label: "Brand", type: "text" },
      { field: "model", label: "Model", type: "text" },
      { field: "port_count", label: "Port Count", type: "number" },
      { field: "usb_type", label: "USB Type", type: "select", options: ["USB-A", "USB-C", "USB-A+C"] },
      { field: "powered", label: "Powered", type: "select", options: ["false", "true"] },
      { field: "price", label: "Price", type: "number" },
      { field: "price_unit", label: "Unit", type: "select", options: ["each", "per_set"] },
      { field: "keywords", label: "Keywords (comma-sep)", type: "text" },
      { field: "notes", label: "Notes", type: "text" },
    ],
  },
  {
    key: "hard_drive", label: "Hard Drive", icon: Storage,
    columns: [
      { field: "brand", label: "Brand" },
      { field: "model", label: "Model" },
      { field: "drive_type", label: "Type" },
      { field: "capacity_gb", label: "Capacity (GB)", numeric: true },
      { field: "form_factor", label: "Form Factor" },
      { field: "price", label: "Price", type: "price" },
    ],
    formFields: [
      { field: "brand", label: "Brand", type: "text" },
      { field: "model", label: "Model", type: "text" },
      { field: "drive_type", label: "Drive Type", type: "select", options: ["HDD", "SSD", "NVMe", "External HDD", "External SSD"] },
      { field: "capacity_gb", label: "Capacity (GB)", type: "number" },
      { field: "form_factor", label: "Form Factor", type: "select", options: ["2.5", "3.5", "M.2"] },
      { field: "price", label: "Price", type: "number" },
      { field: "price_unit", label: "Unit", type: "select", options: ["each", "per_set"] },
      { field: "keywords", label: "Keywords (comma-sep)", type: "text" },
      { field: "notes", label: "Notes", type: "text" },
    ],
  },
];

/** One row of a curated table: an id plus the category's own columns. */
type CuratedRow = { id: number } & Record<string, unknown>;

/** GET /curated-prices/:category */
interface CuratedPage {
  data?: CuratedRow[];
  pagination?: { total: number };
}

// ============================================================================
// Component
// ============================================================================

export default function CuratedPricesManagement() {
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState("");
  const [editDialog, setEditDialog] = useState<{ open: boolean; item: CuratedRow | null }>({ open: false, item: null });
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; item: CuratedRow | null }>({ open: false, item: null });
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [isAddMode, setIsAddMode] = useState(false);

  const category = CATEGORIES[activeTab];

  // Fetch items for active category
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.curatedPrices.list(category.key, page, rowsPerPage, searchQuery),
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page + 1), limit: String(rowsPerPage) });
      if (searchQuery) params.append("search", searchQuery);
      return fetchCuratedPrices<CuratedPage>(category.key, params);
    },
    staleTime: 30000,
    enabled: !!category.key,
  });

  const { createMutation, updateMutation, deleteMutation } = useAdminCrud<Record<string, unknown>>({
    endpoint: `/curated-prices/${category.key}`,
    label: "item",
    invalidate: queryKeys.curatedPrices.category(category.key),
    messages: {
      create: { success: { title: "Created", description: "Item added successfully" }, error: "Failed" },
      update: { success: { title: "Updated", description: "Item updated successfully" }, error: "Failed" },
      delete: { success: { title: "Deleted" }, error: "Failed" },
    },
    onSaved: () => setEditDialog({ open: false, item: null }),
    onDeleted: () => setDeleteDialog({ open: false, item: null }),
  });

  const handleOpenEdit = (item?: CuratedRow) => {
    const init: Record<string, string> = {};
    category.formFields.forEach(f => {
      const val = item ? item[f.field] : "";
      init[f.field] = val === null || val === undefined ? "" : String(val);
    });
    setFormData(init);
    setIsAddMode(!item);
    setEditDialog({ open: true, item: item || null });
  };

  const handleSave = () => {
    const payload: Record<string, unknown> = {};
    let hasError = false;
    for (const f of category.formFields) {
      const val = formData[f.field];
      if (val === "" || val === undefined) continue;
      if (f.type === "number") {
        const parsed = parseFloat(val);
        if (isNaN(parsed) || String(val).trim() === "") {
          toast({ title: 'Error', description: `Invalid number for ${f.label}` });
          hasError = true;
          continue;
        }
        payload[f.field] = parsed;
      }
      else if (f.type === "select") {
        if (val === "true") payload[f.field] = true;
        else if (val === "false") payload[f.field] = false;
        else payload[f.field] = val;
      }
      else payload[f.field] = val;
    }
    if (hasError) return;

    if (isAddMode) createMutation.mutate(payload);
    else if (editDialog.item) updateMutation.mutate({ id: editDialog.item.id, data: payload });
  };

  const handleFormChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const renderCellValue = (item: CuratedRow, col: CategoryConfig["columns"][number]) => {
    const val = item[col.field];
    if (val === null || val === undefined) return "-";
    if (col.type === "price") return formatMoney(val as number | string);
    if (typeof val === "boolean") return val ? "Yes" : "No";
    return String(val);
  };

  const rows = data?.data || [];
  const colCount = category.columns.length + 1;
  const isTight = (col: CategoryConfig["columns"][number]) => col.numeric || col.type === "price";

  return (
    <Page width="full">
      <PageHeader
        title="Curated Static Prices"
        subtitle="Manually curated price tables for major categories. These prices are used by the AI when the generic DB has no match."
        actions={
          <>
            <Tooltip title="Refresh"><IconButton onClick={() => refetch()}><Refresh /></IconButton></Tooltip>
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenEdit()}>
              Add {category.label}
            </Button>
          </>
        }
      />

      <DataCard>
        {/* Category Tabs */}
        <Tabs
          value={activeTab}
          onChange={(_, v) => { setActiveTab(v); setPage(0); setSearchQuery(""); }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ px: 1, borderBottom: 1, borderColor: "divider" }}
        >
          {CATEGORIES.map((cat, i) => {
            const Icon = cat.icon;
            return (
              <Tab
                key={cat.key}
                label={cat.label}
                value={i}
                icon={<Icon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                sx={{ gap: 0.75, px: 2, "& .MuiTab-iconWrapper": { mr: 0 } }}
              />
            );
          })}
        </Tabs>

        {/* Toolbar */}
        <DataToolbar summary={data?.pagination ? `${Number(data.pagination.total).toLocaleString()} ${data.pagination.total === 1 ? "item" : "items"}` : undefined}>
          <SearchField
            placeholder={`Search ${category.label}...`}
            value={searchQuery}
            onChange={(v) => { setSearchQuery(v); setPage(0); }}
          />
        </DataToolbar>

        {error && <Alert severity="error" sx={{ m: 2 }}>{(error as ErrorLike | null)?.response?.data?.error || "Failed to load data"}</Alert>}

        {/* Table */}
        <TableContainer>
          <Table size="small" sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow>
                {category.columns.map(col => (
                  <TableCell
                    key={col.field}
                    align={isTight(col) ? "right" : "left"}
                    sx={isTight(col) ? TIGHT_COL_SX : undefined}
                  >
                    {col.label}
                  </TableCell>
                ))}
                <ActionsHeadCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && !data ? (
                <TableMessageRow colSpan={colCount} loading />
              ) : rows.length === 0 ? (
                <TableMessageRow colSpan={colCount}>No items found. Click "Add" to create one.</TableMessageRow>
              ) : (
                rows.map((item) => (
                  <TableRow key={item.id} hover>
                    {category.columns.map(col => (
                      <TableCell
                        key={col.field}
                        align={isTight(col) ? "right" : "left"}
                        sx={[
                          { whiteSpace: "nowrap" },
                          isTight(col) ? TIGHT_COL_SX : {},
                          col.type === "price" ? { fontWeight: 600 } : {},
                          col.field === "model" ? { fontWeight: 600 } : {},
                        ]}
                      >
                        {renderCellValue(item, col)}
                      </TableCell>
                    ))}
                    <RowActions
                      onEdit={() => handleOpenEdit(item)}
                      onDelete={() => setDeleteDialog({ open: true, item })}
                    />
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {data?.pagination && data.pagination.total > 0 && (
          <DataPagination
            count={data.pagination.total}
            page={page}
            onPageChange={setPage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={n => { setRowsPerPage(n); setPage(0); }}
          />
        )}
      </DataCard>

      {/* Edit / Add Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, item: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{isAddMode ? `Add ${category.label}` : `Edit ${category.label}`}</DialogTitle>
        <DialogContent dividers>
          <Box sx={FORM_GRID_SX}>
            {category.formFields.map(f => (
              f.type === "select" ? (
                <FormControl key={f.field} size="small" fullWidth>
                  <InputLabel>{f.label}</InputLabel>
                  <Select
                    value={formData[f.field] || ""}
                    label={f.label}
                    onChange={e => handleFormChange(f.field, e.target.value)}
                  >
                    {f.options?.map(opt => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  key={f.field}
                  label={f.label}
                  size="small"
                  type={f.type === "number" ? "number" : "text"}
                  value={formData[f.field] || ""}
                  onChange={e => handleFormChange(f.field, e.target.value)}
                  fullWidth
                  sx={f.field === "keywords" || f.field === "notes" ? FULL_SPAN_SX : undefined}
                />
              )
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditDialog({ open: false, item: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={createMutation.isPending || updateMutation.isPending}>
            {isAddMode ? "Create" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, item: null })}>
        <DialogTitle>Delete item?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteDialog({ open: false, item: null })}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => deleteDialog.item && deleteMutation.mutate(deleteDialog.item.id)}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
